import ActivityReportTableDataLoader from 'containers/activity-reports/types/ActivityReportTableDataLoader';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useState } from 'react';
import ITimeRange from 'types/TimeRange';
import TimeRangeType from 'types/TimeRangeType';
import { LaborMetricsInterval } from '__generated__/graphql';
import { ActivityReportType } from '../../ActivityReportFilter/ActivityReportFilter';
import { ICostCodeActivityTableRowInfo } from './useCostCodeActivity';
import useCostCodeActivityBasicDetails from './useCostCodeActivityBasicDetails';
import useCostCodeActivityDateRangeDetails from './useCostCodeActivityDateRangeDetails';
import useCostCodeActivityEquipmentDetails from './useCostCodeActivityEquipmentDetails';
import useCostCodeActivityMemberDetails from './useCostCodeActivityMemberDetails';
import useCostCodeActivityProjectDetails from './useCostCodeActivityProjectDetails';

export default function useCostCodeActivityDetails(
  filterId: string,
  filterType: ActivityReportType,
  secondaryFilterType: ActivityReportType,
  timeRange: ITimeRange<DateTime>,
  filterIdWithDescendants?: boolean,
  dateRangeReportType?: TimeRangeType
): ActivityReportTableDataLoader<ICostCodeActivityTableRowInfo> {
  const [isLoading, setIsLoading] = useState(false);

  const dateRangeInterval = useMemo(() => {
    if (secondaryFilterType === ActivityReportType.BY_DAY) {
      return LaborMetricsInterval.Day;
    }

    if (dateRangeReportType === TimeRangeType.PAY_PERIOD) {
      return LaborMetricsInterval.PayPeriod;
    }

    if (dateRangeReportType === TimeRangeType.MONTHLY) {
      return LaborMetricsInterval.Month;
    }

    return LaborMetricsInterval.OvertimePeriod;
  }, [secondaryFilterType, dateRangeReportType]);

  const basicDetails = useCostCodeActivityBasicDetails(filterId, filterType, timeRange, filterIdWithDescendants);
  const memberDetails = useCostCodeActivityMemberDetails(filterId, filterType, timeRange, filterIdWithDescendants);
  const equipmentDetails = useCostCodeActivityEquipmentDetails(
    filterId,
    filterType,
    timeRange,
    filterIdWithDescendants
  );
  const projectDetails = useCostCodeActivityProjectDetails(filterId, filterType, timeRange);
  const dateRangeDetails = useCostCodeActivityDateRangeDetails(
    filterId,
    filterType,
    timeRange,
    dateRangeInterval,
    filterIdWithDescendants
  );

  const dataLoader = useMemo(() => {
    if (secondaryFilterType === ActivityReportType.BASIC) {
      return basicDetails;
    } else if (secondaryFilterType === ActivityReportType.BY_EMPLOYEE) {
      return memberDetails;
    } else if (secondaryFilterType === ActivityReportType.BY_DATE_RANGE) {
      return dateRangeDetails;
    } else if (secondaryFilterType === ActivityReportType.BY_DAY) {
      return dateRangeDetails;
    } else if (secondaryFilterType === ActivityReportType.BY_EQUIPMENT) {
      return equipmentDetails;
    } else if (secondaryFilterType === ActivityReportType.BY_PROJECT) {
      return projectDetails;
    } else {
      throw Error('Not supported secondary report type when loading cost codes: ' + secondaryFilterType);
    }
  }, [basicDetails, memberDetails, equipmentDetails, projectDetails, dateRangeDetails]);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeRange, filterType, filterId, secondaryFilterType, dateRangeInterval]);

  async function loadData() {
    setIsLoading(true);
    await dataLoader.loadData();
    setIsLoading(false);
  }

  return {
    isLoading: isLoading,
    sortedData: dataLoader.sortedData,
    onSort: dataLoader.onSort,
    sortedBy: dataLoader.sortedBy as keyof ICostCodeActivityTableRowInfo,
    sortDirection: dataLoader.sortDirection,
    sortIsDirty: dataLoader.sortIsDirty,
    remainingData: dataLoader.remainingData,
  };
}
